import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useFocusNext } from "../../../hooks/useFocusNext";
import type { CustomHighlight } from "../PdfTemplate";
import BodySmall from "../../atoms/typography/BodySmall";
import { HorizontalSpacer } from "../../atoms/Spacers";
import styled from "styled-components";
import { theme } from "../../../theme";
import Divider from "../../atoms/Divider";
import Body from "../../atoms/typography/Body";
import IconButton from "../../atoms/buttons/IconButton";
import { ClearIcon } from "../../atoms/icons/ClearIcon";
import { Row } from "react-grid-system";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { ViewOffIcon, ViewOnIcon } from "../../atoms/icons/ViewIcons";
import Checkbox from "../../atoms/inputs/Checkbox";
import type {
  InvoiceReaderTemplateRowExtractionModel,
  PossibleFieldPositionWithValue,
} from "../../../models/ClientInvoiceReaderTemplateModel";
import { useTranslation } from "react-i18next";
import type { Position } from "../../../models/Position";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import type { TemplateTabHandle } from "../../../models/Templates";
import Alert from "../../molecules/Alert";
import {
  resolvePositionWithValue,
  findPageForPosition,
  buildFieldOptionsWithFallback,
  hasDuplicatePositions,
} from "../../../utils/templateHelpers";

type RowWithPage = Omit<
  InvoiceReaderTemplateRowExtractionModel,
  "columnHeader"
> & {
  columnHeader?: { position: Position; value: string; pageNumber?: number };
};
import { useUpdateTemplateRowExtraction } from "../../../graphql/hooks/invoiceInterpretationTemplates/useUpdateTemplateRowExtraction";
import { useGetTextAtPosition } from "../../../graphql/hooks/invoiceInterpretationTemplates/useGetTextAtPosition";
import { stripTypename } from "../../../utils/graphql";
import AutocompleteSelect from "../../atoms/inputs/AutocompleteSelect";
import ToggleAllTextButton from "../../atoms/buttons/ToggleAllTextButton";
import Small from "../../atoms/typography/Small";
import ActiveFieldWrapper from "./ActiveFieldWrapper";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";
import Tooltip from "../../molecules/Tooltip";
import TextField from "../../atoms/inputs/TextField";

interface Props {
  possibleFields: Array<PossibleFieldPositionWithValue>;
  highlightedFields: Array<CustomHighlight>;
  setHighlightedFields: (field: CustomHighlight[]) => void;
  rowExtractionData: Array<InvoiceReaderTemplateRowExtractionModel>;
  ignoredHeaders?: string[];
  onSaveAndContinue: () => void;
  onDirtyChange?: () => void;
  onNavigateToPage?: (page: number) => void;
  totalPages?: number;
  onSaveError?: (message: string) => void;
}

const FlexedDivCentered = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ROW_FIELD_BORDER = "rgba(230, 122, 0, 0.8)";
const ROW_FIELD_BACKGROUND = "rgba(230, 122, 0, 0.15)";

const EditInvoiceRowsField = forwardRef<TemplateTabHandle, Props>(
  (
    {
      possibleFields,
      highlightedFields,
      setHighlightedFields,
      rowExtractionData,
      ignoredHeaders,
      onSaveAndContinue,
      onDirtyChange,
      onNavigateToPage,
      totalPages,
      onSaveError,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { id: clientIdentifier, templateId } = useParams();
    const { getTextAtPosition } = useGetTextAtPosition();

    const initialRows = useMemo(
      () =>
        rowExtractionData.map((r) => ({
          columnHeader: r.columnHeader?.position
            ? {
                position: stripTypename(r.columnHeader.position),
                value: r.columnHeader.value,
                pageNumber: findPageForPosition(
                  r.columnHeader.position,
                  possibleFields,
                ),
              }
            : undefined,
          columnHeaderType: r.columnHeaderType,
          isFixedPosition: r.isFixedPosition ?? false,
        })),
      [rowExtractionData],
    );

    const [rows, setRows] = useState<RowWithPage[]>(initialRows);
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const [ignoredHeadersList, setIgnoredHeadersList] = useState<string[]>(
      ignoredHeaders ?? [],
    );
    const [newHeader, setNewHeader] = useState("");
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    const navigateAfterSaveRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [duplicateWarnings, setDuplicateWarnings] = useState(
      new Set<string>(),
    );
    const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
    const activeRowIndexRef = useRef<number | null>(null);
    activeRowIndexRef.current = activeRowIndex;
    const [ignoredHeaderInputActive, setIgnoredHeaderInputActive] =
      useState(false);
    const ignoredHeaderInputActiveRef = useRef(false);
    ignoredHeaderInputActiveRef.current = ignoredHeaderInputActive;

    const updateWarning = (key: string, value: string | undefined) => {
      setDuplicateWarnings((prev) => {
        const next = new Set(prev);
        if (value && hasDuplicatePositions(value, possibleFields)) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    };

    const highlightedFieldsRef = useRef(highlightedFields);
    highlightedFieldsRef.current = highlightedFields;
    const { formRef, lastFocusedInputRef, focusNextInput } = useFocusNext();

    const { updateTemplateRowExtraction, loading } =
      useUpdateTemplateRowExtraction({
        onError: (e) => {
          const msg = e?.message || t("errors.somethingWentWrong");
          setError(msg);
          onSaveError?.(msg);
        },
        onCompleted: (result) => {
          if (
            result.invoiceInterpretation?.updateTemplateRowExtraction?.success
          ) {
            if (navigateAfterSaveRef.current) {
              onSaveAndContinue();
            }
          } else {
            setError(t("errors.somethingWentWrong"));
          }
        },
      });

    const markDirty = (isDirty: boolean) => {
      dirtyRef.current = isDirty;
      setDirty(isDirty);
      onDirtyChange?.();
    };

    const checkIfFieldHighlighted = (columnHeaderType: string) =>
      highlightedFields.some((h) => h.label === columnHeaderType);

    const onToggleHighlightField = (
      columnHeaderType: string,
      position?: Position,
      pageNumber?: number,
    ) => {
      if (checkIfFieldHighlighted(columnHeaderType)) {
        setHighlightedFields(
          highlightedFields.filter((h) => h.label !== columnHeaderType),
        );
      } else if (position) {
        setHighlightedFields([
          ...highlightedFields,
          {
            id: columnHeaderType,
            position,
            label: columnHeaderType,
            pageNumber,
            color: ROW_FIELD_BORDER,
            background: ROW_FIELD_BACKGROUND,
          },
        ]);
      }
    };

    const areAllHighlighted = () => {
      // If no fields have positions, return false
      const fieldsWithPositions = rows.filter(
        (field) => field.columnHeader?.position,
      );
      if (fieldsWithPositions.length === 0) return false;

      // Check if all fields with positions are highlighted
      return fieldsWithPositions.every((field) =>
        checkIfFieldHighlighted(field.columnHeaderType),
      );
    };

    const toggleAll = () => {
      if (areAllHighlighted()) {
        // Hide all - remove all field highlights
        const fieldsToRemove = rows.map((row) => row.columnHeaderType);
        setHighlightedFields(
          highlightedFields.filter(
            (h) => !h.label || !fieldsToRemove.includes(h.label as any),
          ),
        );
      } else {
        // Show all - add highlights for all fields
        const newHighlights: CustomHighlight[] = [];
        rows.forEach((row) => {
          if (row.columnHeader?.position) {
            newHighlights.push({
              id: row.columnHeaderType,
              position: row.columnHeader.position,
              label: row.columnHeaderType as any,
              pageNumber: row.columnHeader.pageNumber,
              color: ROW_FIELD_BORDER,
              background: ROW_FIELD_BACKGROUND,
            });
          }
        });

        // Remove existing field highlights and add new ones
        const fieldsToRemove = rows.map((row) => row.columnHeaderType);
        const filtered = highlightedFields.filter(
          (h) => !h.label || !fieldsToRemove.includes(h.label as any),
        );
        setHighlightedFields([...filtered, ...newHighlights]);
      }
    };

    const save = async () => {
      if (!dirtyRef.current) return;
      setError(null);

      await updateTemplateRowExtraction({
        rows: rows.map(({ columnHeader, ...rest }) => ({
          ...rest,
          columnHeader: columnHeader
            ? { position: columnHeader.position, value: columnHeader.value }
            : undefined,
        })),
        templateId,
        clientIdentifier: clientIdentifier!,
        ignoredHeaders: ignoredHeadersList,
      });

      markDirty(false);
    };

    useImperativeHandle(ref, () => ({
      save,
      isDirty: () => dirtyRef.current,
      markDirty: () => markDirty(true),
      handleFieldClick: (clickedField) => {
        const idx = activeRowIndexRef.current;

        if (ignoredHeaderInputActiveRef.current) {
          const value = clickedField.value ?? "";
          if (value) {
            setNewHeader(value);
            setIgnoredHeaderInputActive(false);
          }
          return;
        }

        if (idx === null || !clickedField.position) return;
        const columnHeaderType = rowsRef.current[idx]?.columnHeaderType;
        if (!columnHeaderType) return;
        const resolved = {
          value: clickedField.value ?? "",
          position: stripTypename(clickedField.position),
          pageNumber: clickedField.pageNumber,
        };
        setRows((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], columnHeader: resolved };
          return next;
        });
        markDirty(true);
        setActiveRowIndex(null);
        updateWarning(columnHeaderType, clickedField.value);
        const filtered = highlightedFieldsRef.current.filter(
          (h) => h.label !== columnHeaderType,
        );
        setHighlightedFields([
          ...filtered,
          {
            id: columnHeaderType,
            position: resolved.position,
            label: columnHeaderType,
            pageNumber: clickedField.pageNumber,
            color: ROW_FIELD_BORDER,
            background: ROW_FIELD_BACKGROUND,
          },
        ]);
        onNavigateToPage?.(clickedField.pageNumber);
        focusNextInput();
      },
      handleHighlightResized: (
        label: string,
        newPosition: Position,
        pageNumber: number,
      ) => {
        getTextAtPosition({
          templateId: templateId!,
          clientIdentifier: clientIdentifier!,
          pageNumber,
          position: newPosition,
        })
          .then((result) => {
            const fetched =
              result?.data?.invoiceInterpretation?.getTextAtPosition;
            if (!fetched?.position) return;

            const resolved = {
              value: fetched.value ?? "",
              position: stripTypename(fetched.position),
              pageNumber,
            };
            setRows((prev) =>
              prev.map((row) =>
                row.columnHeaderType === label
                  ? { ...row, columnHeader: resolved }
                  : row,
              ),
            );
            setHighlightedFields(
              highlightedFieldsRef.current.map((h) =>
                h.label === label
                  ? { ...h, position: resolved.position, pageNumber }
                  : h,
              ),
            );
            markDirty(true);
          })
          .catch((e: any) => {
            setError(e?.message || t("errors.somethingWentWrong"));
          });
      },
    }));

    const onClickSaveAndContinue = async () => {
      navigateAfterSaveRef.current = true;
      try {
        await save();
      } catch (e: any) {
        setError(e.message || t("errors.somethingWentWrong"));
      } finally {
        navigateAfterSaveRef.current = false;
      }
    };

    return (
      <div ref={formRef}>
        {error && <Alert type="error" title={error} />}
        {rows && rows.length > 0 && (
          <>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="end">
              <ToggleAllTextButton
                onClick={toggleAll}
                showHideAll={areAllHighlighted()}
                showAllText={t("templates.showAll")}
                hideAllText={t("templates.hideAll")}
              />
            </Row>
          </>
        )}

        {rows.map((field, index) => (
          <div key={`${field.columnHeaderType}-${index}`}>
            <HorizontalSpacer spacing={1} />
            <Row nogutter justify="between" align="center">
              <Body>
                {t(`templates.fieldLabels.${field.columnHeaderType}`)}
              </Body>
              <ToggleButton
                onToggle={() =>
                  onToggleHighlightField(
                    field.columnHeaderType,
                    field.columnHeader?.position,
                    field.columnHeader?.pageNumber,
                  )
                }
                isOn={checkIfFieldHighlighted(field.columnHeaderType)}
                size="medium"
                iconOn={<ViewOnIcon color={theme.colors.white} />}
                iconOff={<ViewOffIcon color={theme.colors.primary} />}
                tabIndex={-1}
              />
            </Row>

            <HorizontalSpacer spacing={0.5} />

            <BodySmall
              onClick={() => setActiveRowIndex(index)}
              style={{ cursor: "pointer" }}
            >
              {t("templates.label")}
            </BodySmall>
            <FlexedDivCentered>
              <ActiveFieldWrapper
                $active={activeRowIndex === index}
                onClick={() => setActiveRowIndex(index)}
                onFocus={(e) => {
                  setActiveRowIndex(index);
                  if (e.target instanceof HTMLInputElement)
                    lastFocusedInputRef.current = e.target;
                }}
              >
                <AutocompleteSelect
                  value={field.columnHeader?.value || ""}
                  onChange={(value) => {
                    const selectedValue = value;
                    const resolved = resolvePositionWithValue(
                      selectedValue,
                      possibleFields,
                    );

                    setRows((prev) => {
                      const next = [...prev];
                      next[index] = {
                        ...next[index],
                        columnHeader: resolved
                          ? {
                              value: resolved.value,
                              position: stripTypename(resolved.position),
                              pageNumber: resolved.pageNumber,
                            }
                          : undefined,
                      };
                      return next;
                    });
                    markDirty(true);

                    const filtered = highlightedFields.filter(
                      (h) => h.label !== field.columnHeaderType,
                    );
                    if (resolved) {
                      setHighlightedFields([
                        ...filtered,
                        {
                          id: field.columnHeaderType,
                          position: stripTypename(resolved.position),
                          label: field.columnHeaderType,
                          pageNumber: resolved.pageNumber,
                          color: ROW_FIELD_BORDER,
                          background: ROW_FIELD_BACKGROUND,
                        },
                      ]);
                      onNavigateToPage?.(resolved.pageNumber);
                    } else {
                      setHighlightedFields(filtered);
                    }
                    updateWarning(field.columnHeaderType, resolved?.value);
                    focusNextInput();
                  }}
                  options={buildFieldOptionsWithFallback(
                    possibleFields,
                    totalPages,
                    t("common.page"),
                    t("templates.selectAField"),
                    field.columnHeader?.value,
                  )}
                ></AutocompleteSelect>
              </ActiveFieldWrapper>
              <IconButton
                icon={<ClearIcon />}
                onClick={() => {
                  setRows((prev) => {
                    const next = [...prev];
                    next[index] = {
                      ...next[index],
                      columnHeader: undefined,
                    };
                    return next;
                  });
                  markDirty(true);
                  updateWarning(field.columnHeaderType, undefined);
                }}
                tabIndex={-1}
              />
            </FlexedDivCentered>
            {duplicateWarnings.has(field.columnHeaderType) && (
              <>
                <HorizontalSpacer spacing={0.25} />
                <Small color={theme.colors.textSecondary}>
                  {t("templates.duplicateLabelWarning")}
                </Small>
              </>
            )}

            {(field.columnHeaderType === "END_POSITION" ||
              field.columnHeaderType === "ALTERNATIVE_END_POSITION") && (
              <>
                <HorizontalSpacer spacing={0.25} />
                <Checkbox
                  small
                  title={t("templates.strictPosition")}
                  checked={field.isFixedPosition}
                  onChange={(checked: boolean) => {
                    setRows((prev) => {
                      const next = [...prev];
                      next[index] = {
                        ...next[index],
                        isFixedPosition: checked,
                      };
                      return next;
                    });
                    markDirty(true);
                  }}
                />
                <HorizontalSpacer spacing={0.25} />
                <BodySmall color={theme.colors.textSecondary}>
                  {t("templates.strictPositionDescription")}
                </BodySmall>
              </>
            )}

            <HorizontalSpacer spacing={1} />
            <Divider />
          </div>
        ))}

        <HorizontalSpacer spacing={2} />

        <FlexedDivCentered>
          <Body>{t("templates.ignoredHeaders")}</Body>
          <Tooltip
            text={t("templates.ignoredHeadersTooltip")}
            width="280px"
            direction="right"
          >
            <InfoIconSmall color={theme.colors.textSecondary} />
          </Tooltip>
        </FlexedDivCentered>
        <HorizontalSpacer spacing={0.5} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ignoredHeadersList.map((header, i) => (
            <FlexedDivCentered
              key={i}
              style={{
                borderBottom: `1px solid ${theme.colors.grey05}`,
                paddingBottom: 4,
                alignItems: "flex-end",
              }}
            >
              <BodySmall style={{ flex: 1 }}>{header}</BodySmall>
              <IconButton
                icon={<ClearIcon />}
                onClick={() => {
                  setIgnoredHeadersList((prev) =>
                    prev.filter((_, j) => j !== i),
                  );
                  markDirty(true);
                }}
                size="medium"
              />
            </FlexedDivCentered>
          ))}
        </div>
        <HorizontalSpacer spacing={1} />
        <BodySmall>
          Du kan markera textfältet och sedan klicka i fakturan för att välja
          korrekt rubrik.
        </BodySmall>
        <HorizontalSpacer spacing={1} />
        <FlexedDivCentered style={{ alignItems: "flex-end" }}>
          <ActiveFieldWrapper
            $active={ignoredHeaderInputActive}
            onClick={() => {
              setIgnoredHeaderInputActive(true);
              setActiveRowIndex(null);
            }}
            onFocus={() => {
              setIgnoredHeaderInputActive(true);
              setActiveRowIndex(null);
            }}
          >
            <TextField
              value={newHeader}
              onChange={setNewHeader}
              placeholder={t("templates.ignoredHeaderPlaceholder")}
              fullWidth
            />
          </ActiveFieldWrapper>
          <OutlinedButton
            title={t("common.add")}
            color={theme.colors.primary}
            medium
            onClick={() => {
              const trimmed = newHeader.trim();
              if (!trimmed || ignoredHeadersList.includes(trimmed)) return;
              setIgnoredHeadersList((prev) => [...prev, trimmed]);
              setNewHeader("");
              markDirty(true);
            }}
          />
        </FlexedDivCentered>

        <HorizontalSpacer spacing={2} />
        <Row nogutter justify="end">
          <OutlinedButton
            title={t("random.saveAndContinue")}
            iconRight={<ChevronSmallRightIcon color={theme.colors.primary} />}
            color={theme.colors.primary}
            medium
            onClick={onClickSaveAndContinue}
            disabled={!dirty || loading}
          />
        </Row>
      </div>
    );
  },
);

export default EditInvoiceRowsField;
